import { logs } from '@shoppykey/tools';
import { message } from 'antd';
import { instanceToPlain, plainToInstance } from 'class-transformer';
import { validateOrReject } from 'class-validator';
import { Dayjs, default as dayjs, default as moment } from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import _ from 'lodash';
import { parse } from 'qs';

dayjs.extend(weekday);

export type Route = {
  /**
   * Any valid URL path
   */
  path?: string;
  /**
   * A React component to render only when the location matches.
   */
  component?: string | (() => any);
  wrappers?: string[];
  /**
   * navigate to a new location
   */
  redirect?: string;
  /**
   * When true, the active class/style will only be applied if the location is matched exactly.
   */
  exact?: boolean;
  routes?: Route[];
  [k: string]: any;
};

const reg =
  /(((^https?:(?:\/\/)?)(?:[-;:&=+$,\w]+@)?[A-Za-z0-9.-]+(?::\d+)?|(?:www.|[-;:&=+$,\w]+@)[A-Za-z0-9.-]+)((?:\/[+~%/.\w-_]*)?\??(?:[-+=&;%@.\w_]*)#?(?:[\w]*))?)$/;

export const isUrl = (path: string): boolean => reg.test(path);

export const isAntDesignPro = (): boolean => {
  if (ANT_DESIGN_PRO_ONLY_DO_NOT_USE_IN_YOUR_PRODUCTION === 'site') {
    return true;
  }
  return window.location.hostname === 'preview.pro.ant.design';
};

// 给官方演示站点用，用于关闭真实开发环境不需要使用的特性
export const isAntDesignProOrDev = (): boolean => {
  const { NODE_ENV } = process.env;
  if (NODE_ENV === 'development') {
    return true;
  }
  return isAntDesignPro();
};

export const getPageQuery = () => {
  const { href } = window.location;
  const qsIndex = href.indexOf('?');
  const sharpIndex = href.indexOf('#');

  if (qsIndex !== -1) {
    if (qsIndex > sharpIndex) {
      return parse(href.split('?')[1] ?? '');
    }

    return parse(href.slice(qsIndex + 1, sharpIndex));
  }

  return {};
};
export async function wait(time: number) {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve('');
    }, time);
  });
}

export function reloadPage(): void {
  window.location.reload();
}

export const replaceGoto = () => {
  const urlParams = new URL(window.location.href);
  const params = getPageQuery();
  let { redirect } = params as { redirect: string };
  if (redirect) {
    const redirectUrlParams = new URL(redirect);
    if (redirectUrlParams.origin === urlParams.origin) {
      redirect = redirect.substr(urlParams.origin.length);
      if (redirect.match(/^\/.*#/)) {
        redirect = redirect.substr(redirect.indexOf('#'));
      }
    } else {
      window.history.replaceState({}, 'title', '/');
      reloadPage();
      return;
    }
  }
  const url = urlParams.href.split(urlParams.pathname)[0] + (redirect || '/admin/');
  window.history.replaceState({}, 'title', url);
  reloadPage();
};

export const downloadBlob = (fileName: string, data: Blob) => {
  const blobData = [];
  blobData.push(data);
  const a = document.createElement('a');
  const URL = window.URL || window.webkitURL;
  a.href = URL.createObjectURL(new Blob(blobData));
  a.download = fileName;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
};
export const downloadOOSPdf = (oosUrl?: string, name?: string, isBlob?: boolean) => {
  if (!oosUrl) return;
  if (!name) return;
  try {
    if (isBlob) {
      fetch(oosUrl)
        .then((res) => res.blob())
        .then((blob) => {
          downloadBlob(name, blob);
        });
    } else {
      const oa = document.createElement('a');
      oa.href = oosUrl;
      oa.setAttribute('target', '_blank');
      oa.setAttribute('download', name);
      document.body.appendChild(oa);
      oa.click();
    }
  } catch (e) {
    message.error('下载失败，请检查。');
  }
};
export function toHump(name: string) {
  return name.replace(/_(\w)/g, (all, letter) => {
    return letter.toUpperCase();
  });
}

export function toLine(name: string) {
  return name.replace(/([A-Z])/g, '_$1').toLowerCase();
}

export function openInNewTab(href: string) {
  Object.assign(document.createElement('a'), {
    target: '_blank',
    href,
  }).click();
}

export function captureName(name: string) {
  return name.charAt(0).toUpperCase() + name.slice(1);
}

export function openInTab(href: string) {
  Object.assign(document.createElement('a'), {
    href,
  }).click();
}

export function findParentRoute(route: Route, path: string) {
  const pathNames = _.trim(path, '/').split('/');
  const parentPath = pathNames[0];
  const proute = _.find(route.routes, { name: parentPath });
  const nroute = proute || route;
  return _.find(nroute.routes, { path }) ? null : nroute;
}

export function parseDateRage([left, right]: string[]) {
  return [moment(Number(left)).startOf('day').valueOf(), moment(Number(right)).endOf('day').valueOf()];
}

export function momentToValue([left, right]: Dayjs[]) {
  return [left?.startOf('day').valueOf(), right?.endOf('day').valueOf()];
}

//  https://www.cnblogs.com/intbingbing/p/8631671.html  小数转分数
export function decimalsToFractional(decimals: number | undefined) {
  if (!decimals)
    return {
      numerator: 0,
      denominator: 0,
    };
  const formatDecimals = Number(decimals.toFixed(2));
  let denominator = 100;
  let numerator = formatDecimals * 100;
  let bigger = 0;
  function recursion() {
    bigger = denominator > numerator ? denominator : numerator;
    for (let i = bigger; i > 1; i -= 1) {
      if (Number.isInteger(numerator / i) && Number.isInteger(denominator / i)) {
        numerator /= i;
        denominator /= i;
        recursion();
      }
    }
  }
  recursion();
  return {
    numerator,
    denominator,
  };
}
export function parseDateRageToMoment([left, right]: (number | string)[]) {
  return [moment(left).startOf('day').valueOf(), moment(right).endOf('day').valueOf()];
}

export function getCoverImage(url: string) {
  if (/.*oss.*/.test(url)) {
    return `${url}&x-oss-process=video/snapshot,t_10,m_fast`;
  }
  if (/.*cloudfront.*/.test(url)) {
    return `${url}@coverImage.png`;
  }
  return '';
}

export type Optional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export function bulkSetProperties<T>(
  object: T,
  data: Partial<T> | Readonly<T> | Pick<T, keyof T> | Omit<T, keyof T> | Optional<T, keyof T>,
) {
  if (data) {
    (Object.entries(data) as [keyof T, never][]).forEach(([key, value]) => {
      object[key] = value;
    });
  }
}

export type Class = { new (...args: any[]): any };

export async function verify<T extends Class, F = Readonly<T>>(clazz: T, data: unknown) {
  const obj = plainToInstance(clazz, data);
  await validateOrReject(obj);
  return instanceToPlain(obj) as F;
}

export async function bulkVerify<T extends Class, F = Readonly<T>>(clazz: T, data?: unknown[]) {
  return _.reduce(
    data,
    async (res: Promise<F[]>, item) => {
      const result = await res;
      const itemResult = await verify<T, F>(clazz, item);
      return [...result, itemResult];
    },
    Promise.resolve([]),
  );
}
export function isJSONStr(text: string) {
  if (typeof text !== 'string') {
    return false;
  }
  try {
    const json = JSON.parse(text);
    return typeof json === 'object';
  } catch (error) {
    return false;
  }
}

export function stringifyJSON(data: any, spec?: string | number) {
  try {
    let seedData = data;
    // 这里传入的如果是一个字符串，一般我们先把他序列化一下然后重新压缩
    if (typeof seedData === 'string') {
      seedData = JSON.parse(data);
    }
    return JSON.stringify(seedData, null, spec);
  } catch (error) {
    logs.error(error);
    return '';
  }
}

/**
 * 序列化json字符串
 * @param str
 * @returns
 */
export function parseJson(str: string) {
  try {
    return JSON.parse(str);
  } catch (error) {
    return {};
  }
}

export function randomStr(len: number) {
  return Math.random()
    .toString(32)
    .slice(2, 2 + len)
    .padStart(len, '0');
}

export function generateId(type: string, existIdList?: string[]): string {
  if (_.includes(existIdList, type)) {
    return generateId(`${type}-${randomStr(4)}`, existIdList);
  }
  return type;
}

export function isPhoneNumber(mobile: string) {
  const reg = /^(13[0-9]|14[01456879]|15[0-35-9]|16[2567]|17[0-8]|18[0-9]|19[0-35-9])\d{8}$/;
  // 含有非数字或格式不正确
  if (!reg.test(mobile)) {
    return false;
  }
  return true;
}

export function isEmail(email: string) {
  const reg =
    /((\w+)|(\w+[!#$%&'*+\-,./=?^_`{|}~\w]*[!#$%&'*+\-,/=?^_`{|}~\w]))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9-]+\.)+))([a-zA-Z]{2,10}|[0-9]{1,3})(\]?)/;
  if (!reg.test(email)) {
    return false;
  }
  return true;
}

export function findUrl(value: string) {
  const [pact, v] = value.split('://');
  switch (pact) {
    case 'SysUser':
      return `/admin/organization/sysUser/${v}`;
    case 'CompanyUser':
      return `/admin/company/companyUser/${v}`;
    default:
      return `${pact}${v}`;
  }
}
export function formatUrl(content: string): string {
  const urlReg = /\[([^[\]]*)\]\(([^()]*)\)/;
  const matchResult = content.match(urlReg);
  if (!matchResult) {
    return content;
  }
  const [input, name, href] = matchResult;
  if (!input || name === undefined || href === undefined) {
    return content;
  }
  return formatUrl(content.replace(input, `<a target="_blank" href="${findUrl(href)}">${name}</a>`));
}

/**
 * 通过正则表达式匹配对象的某个值
 * @param data
 * @param key
 * @param multipleFiles 是否是多文件解析, 多文件解析返回的是一个对象数组
 * @returns
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getByMatchKey(data: any, key?: (string | number) | (string | number)[]): any {
  // 如果key已经消耗完那么最终的值也就确认了
  if ((_.isArray(key) && _.isEmpty(key)) || _.isUndefined(key) || _.isNull(key)) {
    return data;
  }
  // 构建一个key的数组
  const keys: (string | number)[] = _.isArray(key) ? key : [key];
  const [_key, ..._keys] = keys;
  // 如果值不再是对象那么寻址完毕
  if (typeof data !== 'object') {
    return undefined;
  }
  // 如果key是数字，数据源是数组，那么这里直接从数组里面取值返回
  if (typeof _key === 'number' && _.isArray(data)) {
    return getByMatchKey(_.get(data, _key), _keys);
  }
  if (typeof _key === 'number') {
    return undefined;
  }
  if (!_key) {
    return undefined;
  }
  const objKeys = _.keys(data);
  const dataIndex = _.find(objKeys, (objKey) => {
    return new RegExp(_key).test(objKey);
  });
  if (dataIndex) {
    return getByMatchKey(_.get(data, dataIndex), _keys);
  }
  return undefined;
}

/**
 * 对象的key为正则表达式匹配某个值
 * @param data
 * @param key
 * @returns
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getByKeyMatch(data: any, key?: (string | number) | (string | number)[]): any {
  // 如果key已经消耗完那么最终的值也就确认了
  if ((_.isArray(key) && _.isEmpty(key)) || _.isUndefined(key) || _.isNull(key)) {
    return data;
  }
  // 构建一个key的数组
  const keys: (string | number)[] = _.isArray(key) ? key : [key];
  const [_key, ..._keys] = keys;
  // 如果值不再是对象那么寻址完毕
  if (typeof data !== 'object') {
    return undefined;
  }
  // 如果key是数字，数据源是数组，那么这里直接从数组里面取值返回
  if (typeof _key === 'number' && _.isArray(data)) {
    return getByMatchKey(_.get(data, _key), _keys);
  }
  if (typeof _key === 'number') {
    return undefined;
  }
  if (!_key) {
    return undefined;
  }
  const objKeys = _.keys(data);
  const dataIndex = _.find(objKeys, (objKey) => {
    return new RegExp(objKey).test(_key);
  });
  if (dataIndex) {
    return getByKeyMatch(_.get(data, dataIndex), _keys);
  }
  return undefined;
}

export function readFile(file: File): Promise<Buffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = () => {
      resolve(reader.result as any as Buffer);
    };
    reader.onerror = (e) => {
      reject(e);
    };
  });
}

/**
 *
 * @param name 获取地址栏参数
 * @returns
 */
export function getQueryString(name: string) {
  const reg = new RegExp('(^|&)' + name + '=([^&]*)(&|$)');
  const r = window.location.search.substr(1).match(reg);
  if (r !== null) return unescape(r[2] ?? '');
  return undefined;
}

export function findQuery(searchParams: URLSearchParams) {
  const _query: any = {};
  searchParams.forEach((value, key) => {
    if (_.has(_query, key)) {
      // 处理数组
      const existenceKey = _query?.[key];
      if (existenceKey instanceof Array) {
        _.set(_query, key, [...existenceKey, value]);
      } else {
        _.set(_query, key, [_query?.[key], value]);
      }
    } else {
      _.set(_query, key, value);
    }
  });
  return _query;
}

export function findQueryBySearch(search: string) {
  return parse(_.trimStart(search, '?'));
}

// 判断一个对象里面是否所有的value均为undefined或者null
export function isEmptyValueObject(val?: Record<string, any>) {
  if (val) {
    const paramsArray = Object.values(val);
    const filterResult = _.filter(paramsArray, (o) => {
      return o !== undefined && o !== null;
    });
    if (!_.isEmpty(filterResult)) {
      return false;
    }
    if (_.isEmpty(filterResult)) {
      return true;
    }
  }
  return;
}

// 判断一个对象里面是否含有value为undefined或者null
export function includeEmptyValueObject(val?: Record<string, any>) {
  if (val) {
    const paramsArray = Object.values(val);
    const filterResult = _.filter(paramsArray, (o) => {
      return o === undefined || o === null;
    });
    if (!_.isEmpty(filterResult)) {
      return true;
    }
    if (_.isEmpty(filterResult)) {
      return false;
    }
  }
  return;
}

// 判断一个数组里面是否含有value为undefined或者null的对象
export function includeEmptyValueArr(val: Record<string, any>[]) {
  const paramsArray = Object.values(val);
  const filterResult = _.filter(paramsArray, (o) => {
    return o === undefined || o === null;
  });
  if (!_.isEmpty(filterResult)) {
    return true;
  }
  if (_.isEmpty(filterResult)) {
    return false;
  }
  return;
}

export function compareVersion(v1: string, v2: string) {
  // eslint-disable-next-line eqeqeq
  if (v1 == v2) {
    return 0;
  }

  const vs1 = v1.split('.').map((a) => parseInt(a));
  const vs2 = v2.split('.').map((a) => parseInt(a));

  const length = Math.min(vs1.length, vs2.length);
  for (let i = 0; i < length; i++) {
    const a = vs1[i] ?? 0;
    const b = vs2[i] ?? 0;
    if (a > b) {
      return 1;
    } else if (a < b) {
      return -1;
    }
  }

  // eslint-disable-next-line eqeqeq
  if (length == vs1.length) {
    return -1;
  } else {
    return 1;
  }
}

export function chromeVersion() {
  const userAgents = window.navigator.userAgent.match(/Chrome\/([0-9,\\.]*)/);
  const version = _.get(userAgents, 1) as string;
  return version;
}

export function weekStr(week: string) {
  switch (week) {
    case 'Mon':
      return '周一';
    case 'Tue':
      return '周二';
    case 'Wed':
      return '周三';
    case 'Thur':
    case 'Thu':
      return '周四';
    case 'Fri':
      return '周五';
    case 'Sat':
      return '周六';
    case 'Sun':
      return '周日';
    default:
      return week;
  }
}

export function weekToEnName(week?: string) {
  switch (week) {
    case '周一':
      return 'Mon';
    case '周二':
      return 'Tue';
    case '周三':
      return 'Wed';
    case '周四':
      return 'Thur';
    case '周五':
      return 'Fri';
    case '周六':
      return 'Sat';
    case '周日':
      return 'Sun';
    default:
      return null;
  }
}

export function beforeWeekStr(week?: string) {
  switch (week) {
    case 'Mon':
      return '周一';
    case 'Tue':
      return '周二';
    case 'Wed':
      return '周三';
    case 'Thur':
      return '周四';
    case 'Fri':
      return '周五';
    case 'Sat':
      return '周六';
    case 'Sun':
      return '周日';
    case 'beforeMon':
      return '上周一';
    case 'beforeTue':
      return '上周二';
    case 'beforeWed':
      return '上周三';
    case 'beforeThur':
      return '上周四';
    case 'beforeFri':
      return '上周五';
    case 'beforeSat':
      return '上周六';
    case 'beforeSun':
      return '上周日';
    default:
      return null;
  }
}

export function beforeWeekStrToEnName(week?: string) {
  switch (week) {
    case '周一':
      return 'Mon';
    case '周二':
      return 'Tue';
    case '周三':
      return 'Wed';
    case '周四':
      return 'Thur';
    case '周五':
      return 'Fri';
    case '周六':
      return 'Sat';
    case '周日':
      return 'Sun';
    case '上周一':
      return 'beforeMon';
    case '上周二':
      return 'beforeTue';
    case '上周三':
      return 'beforeWed';
    case '上周四':
      return 'beforeThur';
    case '上周五':
      return 'beforeFri';
    case '上周六':
      return 'beforeSat';
    case '上周日':
      return 'beforeSun';
    default:
      return null;
  }
}

export function toProperties(data: any, pre?: string, result: any = {}) {
  _.forEach(data, (value, key) => {
    const _key = pre ? [pre, key].join('.') : key;
    if (typeof value === 'object') {
      toProperties(value, _key, result);
    } else {
      result[_key] = value;
    }
  });
  return result;
}

export function format(format: string, ...params: any) {
  return format.replace(/{(\d+)}/g, function (match, index) {
    return typeof params[index] !== 'undefined' ? params[index] : match;
  });
}

// 获取最近七天的时间
export function nearlySevenDays() {
  const end = dayjs().endOf('day').valueOf();
  const start = dayjs().subtract(6, 'day').startOf('day').valueOf();
  return [start, end];
}

// 获取最近30天的时间
export function nearlyThirtyDays() {
  const end = dayjs().endOf('day').valueOf();
  const start = dayjs().subtract(29, 'day').startOf('day').valueOf();
  return [start, end];
}

// 今日
export function rangeToday() {
  const start = dayjs().startOf('day').valueOf();
  const end = dayjs().valueOf();
  return [start, end];
}

// 本周
export function currentRangeWeek() {
  const start = dayjs().startOf('week').day(1).valueOf();
  const end = dayjs().valueOf();
  return [start, end];
}

// 本月
export function currentRangeMonth() {
  const start = dayjs().startOf('month').valueOf();
  // 加一天为了 防止接口mutate的时候拿不到最新创建的值
  const end = dayjs().endOf('month').valueOf();
  return [start, end];
}

// 全年
export function currentRangeYear() {
  const start = dayjs().startOf('year').valueOf();
  const end = dayjs().valueOf();
  return [start, end];
}

export function selectPortTitle(enName?: string, cnName?: string, regionShort?: string) {
  let str = '';
  let country = '';
  if (enName) {
    str += enName;
  }
  if (cnName) {
    str += `(${cnName})`;
  }
  if (regionShort) {
    str += `/${regionShort}`;
  }
  if (regionShort) {
    country = ['countries', regionShort, 'name'].join('.');
  }
  return {
    showPortTitle: str,
    country,
  };
}

// 最近三十天 包含今天
export const timeStampOfThirtyDays = [dayjs().add(-29, 'day').startOf('day').valueOf(), dayjs().endOf('day').valueOf()];

export function weekToShirtWeek(week: string) {
  switch (week) {
    case 'Monday':
    case '星期一':
      return 'Mon';
    case 'Tuesday':
    case '星期二':
      return 'Tue';
    case 'Wednesday':
    case '星期三':
      return 'Wed';
    case 'Thursday':
    case '星期四':
      return 'Thur';
    case 'Friday':
    case '星期五':
      return 'Fri';
    case 'Saturday':
    case '星期六':
      return 'Sat';
    case 'Sunday':
    case '星期日':
      return 'Sun';
    default:
      return '';
  }
}

const weekList = [
  'beforeMon',
  'beforeTue',
  'beforeWed',
  'beforeThur',
  'beforeFri',
  'beforeSat',
  'beforeSun',
  'Mon',
  'Tue',
  'Wed',
  'Thur',
  'Fri',
  'Sat',
  'Sun',
  'nextMon',
  'nextTue',
  'nextWed',
  'nextThur',
  'nextFri',
  'nextSat',
  'nextSun',
];

// 相差的天数
export function findDayNumber(currentTime: number, week: string): undefined | number {
  // 判断当前日期是周几
  const currentWeek = weekToShirtWeek(dayjs(currentTime).format('dddd'));

  // 当前日期再weekList中的位置
  const currentIndex = weekList.indexOf(currentWeek);

  if (week === '开船前四天') {
    return -4;
  }

  if (week === '开船前七天') {
    return -7;
  }

  // week 在weekList中的位置
  const nextWeekIndex = weekList.indexOf(week);

  // if (currentIndex > nextWeekIndex) {
  //   // 上周  不处理
  //   return;
  // }

  if (currentIndex === nextWeekIndex) {
    // 同一天
    return 0;
  }

  const num = nextWeekIndex - currentIndex;
  return num;
}

export function copyPicture(url: string) {
  let canvas = document.createElement('CANVAS') as HTMLCanvasElement | null,
    ctx = canvas?.getContext('2d'),
    img = new Image();
  img.crossOrigin = 'Anonymous';
  img.onload = async function () {
    if (canvas) {
      canvas.height = img.height;
      canvas.width = img.width;
      ctx?.drawImage(img, 0, 0);
      let dataURL = canvas.toDataURL('image/png');

      const data = await fetch(dataURL);
      const blob = await data.blob();
      if (navigator.clipboard === undefined) {
        // 注:此方法只能在localhost跟https协议下可用,http协议下不存在此方法
        message.error('请在https协议下操作');
      } else {
        message.success('复制成功');
      }
      await navigator.clipboard.write([
        // eslint-disable-next-line no-undef
        new ClipboardItem({
          [blob.type]: blob,
        }),
      ]);
    }
  };
  img.src = url;
}

export function sortParamsFormat(
  sort?: Record<string, 'ascend' | 'descend' | null>,
): { sort: string; order: 'ASC' | 'DESC' } | undefined {
  if (!sort) {
    return undefined;
  }
  const sortKey = Object.keys(sort)[0];
  if (!sortKey) {
    return undefined;
  }
  return {
    sort: sortKey,
    order: sort[sortKey] === 'descend' ? 'DESC' : 'ASC',
  };
}

export const arrayMove = (data: any[], from: number, to: number) => {
  const array = [...data];

  const startIndex = from < 0 ? array.length + from : from;

  if (startIndex >= 0 && startIndex < array.length) {
    const endIndex = to < 0 ? array.length + to : to;

    const [item] = array.splice(from, 1);
    array.splice(endIndex, 0, item);
  }
  return [...array];
};

export function addBracket(price?: number | string) {
  if (!price) return '';

  if (typeof price === 'string') {
    if (price && Number(price) < 0) {
      return `(${Math.abs(Number(price))})`;
    }
    return price ? `${price}` : '';
  } else {
    if (price && price < 0) {
      return `(${Math.abs(price)})`;
    }
    return price ? `${price}` : '';
  }
}

// 合并对象
export function mergeObjects(obj1?: object, obj2?: object) {
  return _.mergeWith({}, obj2, obj1, (objValue, srcValue) => {
    if (srcValue !== undefined && srcValue !== null) {
      return srcValue;
    }
    return objValue;
  });
}

/**
 * 从一个简单的、不含嵌套的字符串中提取 { 和 } 之间的内容。
 * @param {string} text - 输入的文本。
 * @returns {string|null} - 提取到的内容或 null。
 */
export function extractSimpleJsonString(text: string) {
  try {
    // 找到第一个 '{' 的索引
    const startIndex = text.indexOf('{');
    // 从找到 '{' 的位置开始，查找第一个 '}' 的索引
    const endIndex = text.indexOf('}', startIndex);

    // 如果都找到了（索引不为 -1）
    if (startIndex !== -1 && endIndex !== -1) {
      // 截取并返回，包含 { 和 }
      // .slice(start, end) 提取的范围是 [start, end)
      return text.slice(startIndex, endIndex + 1);
    } else {
      return null; // 或者返回空字符串 ""
    }
  } catch (e) {
    logs.error(e);
    return null;
  }
}

export function formatMonth(month: number) {
  return month < 10 ? `0${month}` : `${month}`;
}

export function currentMonthTime() {
  return [dayjs().startOf('month').valueOf(), dayjs().endOf('month').valueOf()];
}
